import { useMemo, useState } from "react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import FilterBar from "@/components/FilterBar";
import { FileSpreadsheet, Banknote, Shield, Calculator, Landmark, Info } from "lucide-react";

const downloadFile = async (url, filename) => {
  const res = await api.get(url, { responseType: "blob" });
  const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(blobUrl);
};

function fyStartFromMonthYear(month, year) {
  return month >= 4 ? year : year - 1;
}

function fyLabel(fyStart) {
  return `FY${fyStart}-${String(fyStart + 1).slice(-2)}`;
}

function buildFyOptions(aroundStart) {
  const opts = [];
  for (let s = aroundStart - 3; s <= aroundStart + 2; s++) {
    opts.push({
      value: String(s),
      label: `${fyLabel(s)} (Apr ${s} – Mar ${s + 1})`,
    });
  }
  return opts;
}

const EMP_TYPE_OPTIONS = [
  { value: "all", label: "All employment types" },
  { value: "Full-time", label: "Full-time only" },
  { value: "Part-time", label: "Part-time only" },
  { value: "Contract", label: "Contract only" },
  { value: "Intern", label: "Intern only" },
];

const typeQS = (employmentType) =>
  employmentType && employmentType !== "all" ? `&employment_type=${encodeURIComponent(employmentType)}` : "";

const DlButton = ({ onClick, testid, label, primary }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={
      primary
        ? "bg-[#226DB4] text-white hover:bg-[#1A4A7A] transition px-4 py-2 text-sm rounded font-medium inline-flex items-center gap-2"
        : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 transition px-4 py-2 text-sm rounded font-medium inline-flex items-center gap-2"
    }
  >
    <FileSpreadsheet className="h-4 w-4" /> {label}
  </button>
);

const ReportCard = ({ icon: Icon, accent, title, what, sheets, children }) => (
  <div className="bg-white border border-slate-200 rounded-lg overflow-hidden flex flex-col">
    <div className={`h-1 ${accent}`} />
    <div className="p-5 flex flex-col flex-1 gap-3">
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-slate-100 p-2 text-slate-700">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          <p className="text-sm text-slate-600 mt-1">{what}</p>
        </div>
      </div>
      {sheets && (
        <ul className="text-xs text-slate-500 space-y-1 border-t border-slate-100 pt-3 list-disc pl-4">
          {sheets.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
      )}
      <div className="mt-auto flex flex-wrap gap-2 pt-2">{children}</div>
    </div>
  </div>
);

export default function StatutoryReports() {
  const today = new Date();
  const defaultFy = fyStartFromMonthYear(today.getMonth() + 1, today.getFullYear());
  const [periodMode, setPeriodMode] = useState("monthly"); // monthly | fy
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [fyStart, setFyStart] = useState(defaultFy);
  const [employmentType, setEmploymentType] = useState("all");

  const mm = String(month).padStart(2, "0");
  const fy = fyLabel(fyStart);
  const fyOptions = useMemo(() => buildFyOptions(defaultFy), [defaultFy]);
  const tq = typeQS(employmentType);
  const isMonthly = periodMode === "monthly";

  return (
    <AdminLayout title="Statutory reports" breadcrumbs={["Home", "Payroll", "Statutory"]}>
      <div className="space-y-5">
        {/* Mode */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider text-slate-500 mr-2">Period</span>
            <button
              type="button"
              onClick={() => setPeriodMode("monthly")}
              className={`px-4 py-2 text-sm rounded font-medium border transition ${
                isMonthly
                  ? "bg-[#226DB4] text-white border-[#226DB4]"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >
              Monthly filing
            </button>
            <button
              type="button"
              onClick={() => setPeriodMode("fy")}
              className={`px-4 py-2 text-sm rounded font-medium border transition ${
                !isMonthly
                  ? "bg-[#226DB4] text-white border-[#226DB4]"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >
              Financial year pack
            </button>
          </div>

          <FilterBar
            filters={
              isMonthly
                ? [
                    { type: "month", key: "month", label: "Month", value: month, onChange: setMonth },
                    { type: "year", key: "year", label: "Year", value: year, onChange: setYear },
                    {
                      type: "select",
                      key: "empType",
                      label: "Employment type (pre-filter export)",
                      value: employmentType,
                      onChange: setEmploymentType,
                      options: EMP_TYPE_OPTIONS,
                    },
                  ]
                : [
                    {
                      type: "select",
                      key: "fy",
                      label: "Financial year (Apr–Mar)",
                      value: String(fyStart),
                      onChange: (v) => setFyStart(Number(v)),
                      options: fyOptions,
                    },
                    {
                      type: "select",
                      key: "empType",
                      label: "Employment type (pre-filter export)",
                      value: employmentType,
                      onChange: setEmploymentType,
                      options: EMP_TYPE_OPTIONS,
                    },
                  ]
            }
          />

          <p className="text-sm text-slate-600">
            {isMonthly ? (
              <>
                Downloads for <strong>{new Date(2000, month - 1).toLocaleString("default", { month: "long" })} {year}</strong>
                {employmentType !== "all" ? (
                  <> · pre-filtered to <strong>{employmentType}</strong></>
                ) : (
                  <> · all employment types (you can still filter in Excel)</>
                )}
              </>
            ) : (
              <>
                Downloads for <strong>{fy}</strong> (Apr {fyStart} – Mar {fyStart + 1})
                {employmentType !== "all" ? (
                  <> · pre-filtered to <strong>{employmentType}</strong></>
                ) : (
                  <> · all employment types included</>
                )}
              </>
            )}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ReportCard
            icon={Banknote}
            accent="bg-[#226DB4]"
            title="PF — EPFO ECR"
            what="EPF wages and employee / employer contributions for challan preparation."
            sheets={
              isMonthly
                ? ["One sheet of members for the selected month", "Footer totals update when you filter in Excel"]
                : ["All months combined + one tab per month", "Filter by Employment Type or Period in Excel"]
            }
          >
            {isMonthly ? (
              <DlButton
                primary
                testid="dl-pf-ecr"
                label="Download month"
                onClick={() =>
                  downloadFile(
                    `/exports/pf-ecr.xlsx?month=${month}&year=${year}${tq}`,
                    `pf_ecr_${year}_${mm}.xlsx`
                  )
                }
              />
            ) : (
              <DlButton
                primary
                testid="dl-pf-ecr-fy"
                label={`Download ${fy} pack`}
                onClick={() =>
                  downloadFile(
                    `/exports/pf-ecr-fy.xlsx?fy_start_year=${fyStart}${tq}`,
                    `pf_ecr_${fy}.xlsx`
                  )
                }
              />
            )}
          </ReportCard>

          <ReportCard
            icon={Shield}
            accent="bg-emerald-600"
            title="ESIC"
            what="Eligible employees only (gross ≤ ₹21,000) with EE / ER split."
            sheets={
              isMonthly
                ? ["Monthly eligible list", "Filter-aware footer totals"]
                : ["Full FY eligible lines + monthly tabs"]
            }
          >
            {isMonthly ? (
              <DlButton
                primary
                testid="dl-esic"
                label="Download month"
                onClick={() =>
                  downloadFile(
                    `/exports/esic.xlsx?month=${month}&year=${year}${tq}`,
                    `esic_${year}_${mm}.xlsx`
                  )
                }
              />
            ) : (
              <DlButton
                primary
                testid="dl-esic-fy"
                label={`Download ${fy} pack`}
                onClick={() =>
                  downloadFile(
                    `/exports/esic-fy.xlsx?fy_start_year=${fyStart}${tq}`,
                    `esic_${fy}.xlsx`
                  )
                }
              />
            )}
          </ReportCard>

          <ReportCard
            icon={Calculator}
            accent="bg-amber-500"
            title="TDS"
            what="PAN, gross, rate and TDS deducted — Form 24Q preparation support."
            sheets={
              isMonthly
                ? ["Monthly TDS lines"]
                : ["All months in FY + monthly tabs"]
            }
          >
            {isMonthly ? (
              <DlButton
                primary
                testid="dl-tds"
                label="Download month"
                onClick={() =>
                  downloadFile(
                    `/exports/tds-summary.xlsx?month=${month}&year=${year}${tq}`,
                    `tds_summary_${year}_${mm}.xlsx`
                  )
                }
              />
            ) : (
              <DlButton
                primary
                testid="dl-tds-fy"
                label={`Download ${fy} pack`}
                onClick={() =>
                  downloadFile(
                    `/exports/tds-summary-fy.xlsx?fy_start_year=${fyStart}${tq}`,
                    `tds_summary_${fy}.xlsx`
                  )
                }
              />
            )}
          </ReportCard>

          <ReportCard
            icon={Landmark}
            accent="bg-violet-600"
            title="Professional Tax"
            what="State-wise slabs. Requires Gender + work place (Head Office / Branch / Other). Optional PT State override for Other / WFH."
            sheets={
              isMonthly
                ? [
                    "Employee lines = people (audit)",
                    "Slab counts (CA) = headcount & tax by type × slab — use this for filing",
                    "Footer TOTAL changes when you filter Employment Type",
                  ]
                : [
                    "Year slab totals (CA) = Apr–Mar rolled-up counts (person-months)",
                    "All employee lines = every person-month for the year",
                    "Plus one slab tab per month",
                  ]
            }
          >
            {isMonthly ? (
              <DlButton
                primary
                testid="dl-pt"
                label="Download month"
                onClick={() =>
                  downloadFile(
                    `/exports/pt-return.xlsx?month=${month}&year=${year}${tq}`,
                    `pt_return_${year}_${mm}.xlsx`
                  )
                }
              />
            ) : (
              <DlButton
                primary
                testid="dl-pt-fy"
                label={`Download ${fy} pack`}
                onClick={() =>
                  downloadFile(
                    `/exports/pt-return-fy.xlsx?fy_start_year=${fyStart}${tq}`,
                    `pt_return_${fy}.xlsx`
                  )
                }
              />
            )}
          </ReportCard>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-700 space-y-3">
          <div className="flex gap-2 items-start">
            <Info className="h-4 w-4 mt-0.5 text-[#226DB4] shrink-0" />
            <div className="space-y-2">
              <p>
                <strong>Why didn’t Excel totals change when filtering?</strong> Old files used a fixed TOTAL row.
                New downloads use Excel <code>SUBTOTAL</code> — after filtering (e.g. Full-time), the footer recounts only visible rows.
                Or pre-filter with the Employment type dropdown above before download.
              </p>
              <p>
                <strong>PT sheets explained:</strong>{" "}
                <em>Employee lines</em> = who paid what.{" "}
                <em>Slab counts (CA)</em> / <em>Year slab totals</em> = how many people in each tax band (what the return needs).
                They are different views of the same data, not duplicates of each other.
              </p>
              <p>
                <strong>FY pack:</strong> builds every month Apr–Mar that has payslips, then a combined “All months” (or year totals for PT). Empty months simply add nothing.
              </p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
